/**
 * Laptops Collection - Filtering and Comparison
 */

(function() {
  'use strict';

  // State
  const state = {
    filters: {},
    priceRange: { min: 0, max: Infinity },
    comparison: new Map(),
    maxCompareItems: 4
  };

  // DOM Elements
  let productList;
  let comparisonBar;
  let comparisonModal;
  let comparisonCount;
  let activeFiltersContainer;

  /**
   * Initialize the collection functionality
   */
  function init() {
    productList = document.getElementById('product-list');
    comparisonBar = document.getElementById('comparison-bar');
    comparisonModal = document.getElementById('comparison-modal');
    comparisonCount = document.getElementById('comparison-count');
    activeFiltersContainer = document.getElementById('active-filters');

    if (!productList) return;

    // Store product data for filtering
    storeProductData();

    // Initialize filters
    initFilterDropdowns();
    initPriceSlider();

    // Initialize comparison
    initComparison();

    // Initialize variant selectors
    initVariantSelectors();

    // Load saved comparison from sessionStorage
    loadSavedComparison();

    // Initialize infinite scroll
    initInfiniteScroll();

    // Initialize search (if search bar present instead of filters)
    initCollectionSearch();

    // Apply filters from URL parameters (e.g. ?brand=Apple)
    applyUrlFilters();
  }

  /**
   * Store product data in data attributes for filtering
   */
  function storeProductData() {
    const products = productList.querySelectorAll('.laptop-card');

    products.forEach(product => {
      const specs = product.querySelector('.laptop-card__specs');
      const compareCheckbox = product.querySelector('.laptop-card__compare-checkbox');

      if (compareCheckbox) {
        // Store filter data from checkbox data attributes
        product.dataset.brand = compareCheckbox.dataset.productBrand || '';
        product.dataset.cpu = compareCheckbox.dataset.productCpu || '';
        product.dataset.ram = compareCheckbox.dataset.productRam || '';
        product.dataset.storage = compareCheckbox.dataset.productStorage || '';
        product.dataset.screensize = compareCheckbox.dataset.productScreen || '';
        product.dataset.gpu = compareCheckbox.dataset.productGpu || '';
        product.dataset.type = compareCheckbox.dataset.productType || '';

        // Get price from the price element
        const priceEl = product.querySelector('.laptop-card__price-current');
        if (priceEl) {
          const priceText = priceEl.textContent.replace(/[^0-9.,]/g, '').replace(',', '.');
          product.dataset.price = parseFloat(priceText) || 0;
        }

        // Get labels from tags
        const badges = product.querySelectorAll('.laptop-card__badge');
        const labels = [];
        badges.forEach(badge => {
          const classList = badge.className;
          if (classList.includes('--budget')) labels.push('Budget');
          if (classList.includes('--grafik')) labels.push('Grafikperf.');
          if (classList.includes('--performance')) labels.push('Gesamtperf.');
          if (classList.includes('--leicht')) labels.push('Leicht');
          if (classList.includes('--nachhaltig')) labels.push('Nachhaltig');
        });
        product.dataset.labels = labels.join(',');
      }
    });
  }

  /**
   * Initialize filter dropdowns
   */
  function initFilterDropdowns() {
    const filterToggles = document.querySelectorAll('.laptops-filter__toggle');

    filterToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        const dropdown = toggle.nextElementSibling;
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

        // Close all other dropdowns
        filterToggles.forEach(otherToggle => {
          if (otherToggle !== toggle) {
            otherToggle.setAttribute('aria-expanded', 'false');
            const otherDropdown = otherToggle.nextElementSibling;
            if (otherDropdown) otherDropdown.hidden = true;
          }
        });

        // Toggle current dropdown
        toggle.setAttribute('aria-expanded', !isExpanded);
        if (dropdown) dropdown.hidden = isExpanded;
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.laptops-filter')) {
        filterToggles.forEach(toggle => {
          toggle.setAttribute('aria-expanded', 'false');
          const dropdown = toggle.nextElementSibling;
          if (dropdown) dropdown.hidden = true;
        });
      }
    });

    // Initialize filter checkboxes
    const filterCheckboxes = document.querySelectorAll('.laptops-filter__checkbox');
    filterCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        updateFilters();
        applyFilters();
        updateActiveFilters();
        updateFilterCounts();
      });
    });
  }

  /**
   * Initialize price slider
   */
  function initPriceSlider() {
    const minSlider = document.getElementById('price-min');
    const maxSlider = document.getElementById('price-max');
    const minInput = document.getElementById('price-min-input');
    const maxInput = document.getElementById('price-max-input');
    const rangeTrack = document.getElementById('price-range');
    const applyButton = document.getElementById('apply-price-filter');

    if (!minSlider || !maxSlider) return;

    // Get min/max prices from products
    const products = productList.querySelectorAll('.laptop-card');
    let minPrice = Infinity;
    let maxPrice = 0;

    products.forEach(product => {
      const price = parseFloat(product.dataset.price) || 0;
      if (price > 0) {
        minPrice = Math.min(minPrice, price);
        maxPrice = Math.max(maxPrice, price);
      }
    });

    if (minPrice === Infinity) minPrice = 0;
    if (maxPrice === 0) maxPrice = 5000;

    // Round to nice numbers
    minPrice = Math.floor(minPrice / 100) * 100;
    maxPrice = Math.ceil(maxPrice / 100) * 100;

    // Set slider attributes
    minSlider.min = minPrice;
    minSlider.max = maxPrice;
    minSlider.value = minPrice;
    maxSlider.min = minPrice;
    maxSlider.max = maxPrice;
    maxSlider.value = maxPrice;
    minInput.min = minPrice;
    minInput.max = maxPrice;
    minInput.value = minPrice;
    maxInput.min = minPrice;
    maxInput.max = maxPrice;
    maxInput.value = maxPrice;

    function updateRangeTrack() {
      const min = parseInt(minSlider.value);
      const max = parseInt(maxSlider.value);
      const range = maxSlider.max - minSlider.min;
      const left = ((min - minSlider.min) / range) * 100;
      const width = ((max - min) / range) * 100;

      if (rangeTrack) {
        rangeTrack.style.left = left + '%';
        rangeTrack.style.width = width + '%';
      }
    }

    minSlider.addEventListener('input', () => {
      const min = parseInt(minSlider.value);
      const max = parseInt(maxSlider.value);
      if (min > max) minSlider.value = max;
      minInput.value = minSlider.value;
      updateRangeTrack();
    });

    maxSlider.addEventListener('input', () => {
      const min = parseInt(minSlider.value);
      const max = parseInt(maxSlider.value);
      if (max < min) maxSlider.value = min;
      maxInput.value = maxSlider.value;
      updateRangeTrack();
    });

    minInput.addEventListener('change', () => {
      minSlider.value = minInput.value;
      updateRangeTrack();
    });

    maxInput.addEventListener('change', () => {
      maxSlider.value = maxInput.value;
      updateRangeTrack();
    });

    if (applyButton) {
      applyButton.addEventListener('click', () => {
        state.priceRange.min = parseInt(minSlider.value);
        state.priceRange.max = parseInt(maxSlider.value);
        applyFilters();
        updateActiveFilters();

        // Close the dropdown
        const priceFilter = document.querySelector('[data-filter="price"]');
        if (priceFilter) {
          const toggle = priceFilter.querySelector('.laptops-filter__toggle');
          const dropdown = priceFilter.querySelector('.laptops-filter__dropdown');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
          if (dropdown) dropdown.hidden = true;
        }
      });
    }

    updateRangeTrack();
  }

  /**
   * Update filter state from checkboxes
   */
  function updateFilters() {
    state.filters = {};

    const checkboxes = document.querySelectorAll('.laptops-filter__checkbox:checked');
    checkboxes.forEach(checkbox => {
      const type = checkbox.dataset.filterType;
      const value = checkbox.dataset.filterValue;

      if (!state.filters[type]) {
        state.filters[type] = [];
      }
      state.filters[type].push(value);
    });
  }

  /**
   * Apply filters to products
   */
  function applyFilters() {
    const products = productList.querySelectorAll('.laptop-card');
    let visibleCount = 0;

    products.forEach(product => {
      let visible = true;

      // Check each filter type
      for (const [filterType, filterValues] of Object.entries(state.filters)) {
        if (filterValues.length === 0) continue;

        let productValue = '';

        switch (filterType) {
          case 'brand':
            productValue = product.dataset.brand;
            break;
          case 'cpu':
            productValue = product.dataset.cpu;
            break;
          case 'ram':
            productValue = product.dataset.ram;
            break;
          case 'storage':
            productValue = product.dataset.storage;
            break;
          case 'screensize':
            productValue = product.dataset.screensize;
            break;
          case 'screenresolution':
            productValue = product.dataset.screenresolution;
            break;
          case 'type':
            productValue = product.dataset.type;
            break;
          case 'labels':
            productValue = product.dataset.labels;
            // For labels, check if any of the product's labels match
            const productLabels = productValue.split(',');
            const hasMatchingLabel = filterValues.some(fv => productLabels.includes(fv));
            if (!hasMatchingLabel) visible = false;
            continue;
        }

        if (!filterValues.includes(productValue)) {
          visible = false;
        }
      }

      // Check price range
      const price = parseFloat(product.dataset.price) || 0;
      if (price < state.priceRange.min || price > state.priceRange.max) {
        visible = false;
      }

      // Show/hide product
      product.style.display = visible ? '' : 'none';
      if (visible) visibleCount++;
    });

    // Show empty state if no products visible
    const emptyState = document.querySelector('.laptops-products__empty');
    if (emptyState) {
      emptyState.style.display = visibleCount === 0 ? 'block' : 'none';
    }
  }

  /**
   * Update filter counts in dropdowns
   */
  function updateFilterCounts() {
    const filterToggles = document.querySelectorAll('[data-filter-count]');

    filterToggles.forEach(countEl => {
      const filterId = countEl.dataset.filterCount;
      const filterContainer = document.getElementById('filter-' + filterId);
      if (!filterContainer) return;

      const checkedCount = filterContainer.querySelectorAll('.laptops-filter__checkbox:checked').length;
      countEl.textContent = '(' + checkedCount + ')';
      countEl.hidden = checkedCount === 0;
    });
  }

  /**
   * Update active filters display
   */
  function updateActiveFilters() {
    if (!activeFiltersContainer) return;

    let html = '';
    let hasFilters = false;

    // Add filter tags
    for (const [filterType, filterValues] of Object.entries(state.filters)) {
      filterValues.forEach(value => {
        hasFilters = true;
        html += `
          <button type="button" class="active-filter-tag" data-filter-type="${filterType}" data-filter-value="${value}">
            ${value}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        `;
      });
    }

    // Add price range tag if not default
    const minSlider = document.getElementById('price-min');
    const maxSlider = document.getElementById('price-max');
    if (minSlider && maxSlider) {
      const defaultMin = parseInt(minSlider.min);
      const defaultMax = parseInt(maxSlider.max);
      if (state.priceRange.min > defaultMin || state.priceRange.max < defaultMax) {
        hasFilters = true;
        html += `
          <button type="button" class="active-filter-tag" data-filter-type="price">
            €${state.priceRange.min} – €${state.priceRange.max}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        `;
      }
    }

    // Add clear all button
    if (hasFilters) {
      html += `
        <button type="button" class="active-filter-clear" id="clear-all-filters">
          Alle Filter löschen
        </button>
      `;
    }

    activeFiltersContainer.innerHTML = html;

    // Add event listeners to remove buttons
    activeFiltersContainer.querySelectorAll('.active-filter-tag').forEach(tag => {
      tag.addEventListener('click', () => {
        const type = tag.dataset.filterType;
        const value = tag.dataset.filterValue;

        if (type === 'price') {
          // Reset price range
          const minSlider = document.getElementById('price-min');
          const maxSlider = document.getElementById('price-max');
          const minInput = document.getElementById('price-min-input');
          const maxInput = document.getElementById('price-max-input');

          if (minSlider && maxSlider) {
            minSlider.value = minSlider.min;
            maxSlider.value = maxSlider.max;
            if (minInput) minInput.value = minSlider.min;
            if (maxInput) maxInput.value = maxSlider.max;
            state.priceRange.min = parseInt(minSlider.min);
            state.priceRange.max = parseInt(maxSlider.max);
          }
        } else {
          // Uncheck the corresponding checkbox
          const checkbox = document.querySelector(
            `.laptops-filter__checkbox[data-filter-type="${type}"][data-filter-value="${value}"]`
          );
          if (checkbox) checkbox.checked = false;
        }

        updateFilters();
        applyFilters();
        updateActiveFilters();
        updateFilterCounts();
      });
    });

    // Clear all button
    const clearAllBtn = document.getElementById('clear-all-filters');
    if (clearAllBtn) {
      clearAllBtn.addEventListener('click', clearAllFilters);
    }
  }

  /**
   * Clear all filters
   */
  function clearAllFilters() {
    // Uncheck all checkboxes
    document.querySelectorAll('.laptops-filter__checkbox').forEach(cb => {
      cb.checked = false;
    });

    // Reset price
    const minSlider = document.getElementById('price-min');
    const maxSlider = document.getElementById('price-max');
    const minInput = document.getElementById('price-min-input');
    const maxInput = document.getElementById('price-max-input');

    if (minSlider && maxSlider) {
      minSlider.value = minSlider.min;
      maxSlider.value = maxSlider.max;
      if (minInput) minInput.value = minSlider.min;
      if (maxInput) maxInput.value = maxSlider.max;
      state.priceRange.min = 0;
      state.priceRange.max = Infinity;
    }

    state.filters = {};
    applyFilters();
    updateActiveFilters();
    updateFilterCounts();
  }

  /**
   * Initialize comparison functionality
   */
  function initComparison() {
    const checkboxes = document.querySelectorAll('.laptop-card__compare-checkbox:not([data-init])');

    checkboxes.forEach(checkbox => {
      checkbox.setAttribute('data-init', '1');
      checkbox.addEventListener('change', () => {
        const productId = checkbox.dataset.productId;

        if (checkbox.checked) {
          if (state.comparison.size >= state.maxCompareItems) {
            checkbox.checked = false;
            alert(`Sie können maximal ${state.maxCompareItems} Produkte vergleichen.`);
            return;
          }

          state.comparison.set(productId, {
            id: productId,
            title: checkbox.dataset.productTitle,
            image: checkbox.dataset.productImage,
            url: checkbox.dataset.productUrl,
            price: checkbox.dataset.productPrice,
            brand: checkbox.dataset.productBrand,
            cpu: checkbox.dataset.productCpu,
            ram: checkbox.dataset.productRam,
            storage: checkbox.dataset.productStorage,
            screen: checkbox.dataset.productScreen,
            gpu: checkbox.dataset.productGpu
          });
        } else {
          state.comparison.delete(productId);
        }

        updateComparisonUI();
        saveComparison();
      });
    });

    // View comparison button
    const viewBtn = document.getElementById('view-comparison');
    if (viewBtn) {
      viewBtn.addEventListener('click', showComparisonModal);
    }

    // Clear comparison button
    const clearBtn = document.getElementById('clear-comparison');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        state.comparison.clear();
        document.querySelectorAll('.laptop-card__compare-checkbox').forEach(cb => {
          cb.checked = false;
        });
        updateComparisonUI();
        saveComparison();
      });
    }

    // Close modal
    const closeBtn = document.getElementById('close-comparison');
    if (closeBtn) {
      closeBtn.addEventListener('click', hideComparisonModal);
    }

    // Close on overlay click
    const overlay = document.querySelector('.comparison-modal__overlay');
    if (overlay) {
      overlay.addEventListener('click', hideComparisonModal);
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && comparisonModal && comparisonModal.classList.contains('is-visible')) {
        hideComparisonModal();
      }
    });
  }

  /**
   * Update comparison UI
   */
  function updateComparisonUI() {
    const count = state.comparison.size;

    if (comparisonCount) {
      comparisonCount.textContent = count;
    }

    // Only show bar when 2 or more products are selected
    if (comparisonBar) {
      if (count >= 2) {
        comparisonBar.classList.add('is-visible');
      } else {
        comparisonBar.classList.remove('is-visible');
      }
    }
  }

  /**
   * Show comparison modal
   */
  function showComparisonModal() {
    if (!comparisonModal) return;

    const body = document.getElementById('comparison-body');
    if (!body) return;

    if (state.comparison.size === 0) {
      body.innerHTML = '<p>Keine Produkte zum Vergleichen ausgewählt.</p>';
    } else {
      let html = '<div class="comparison-table-wrapper"><table class="comparison-table">';

      // Header row with product images and titles
      html += '<thead><tr><th></th>';
      state.comparison.forEach(product => {
        html += `
          <th>
            <a href="${product.url}" class="comparison-product">
              <img src="${product.image}" alt="${product.title}" class="comparison-product__image">
              <span class="comparison-product__brand">${product.brand}</span>
              <span class="comparison-product__title">${product.title}</span>
            </a>
          </th>
        `;
      });
      html += '</tr></thead>';

      // Body rows with specs
      html += '<tbody>';

      // Price
      html += '<tr><th>Preis</th>';
      state.comparison.forEach(product => {
        html += `<td><strong>${product.price}</strong></td>`;
      });
      html += '</tr>';

      // CPU
      html += '<tr><th>Hauptprozessor</th>';
      state.comparison.forEach(product => {
        html += `<td>${product.cpu || '-'}</td>`;
      });
      html += '</tr>';

      // RAM
      html += '<tr><th>Arbeitsspeicher</th>';
      state.comparison.forEach(product => {
        html += `<td>${product.ram || '-'}</td>`;
      });
      html += '</tr>';

      // Storage
      html += '<tr><th>Massenspeicher</th>';
      state.comparison.forEach(product => {
        html += `<td>${product.storage || '-'}</td>`;
      });
      html += '</tr>';

      // Screen
      html += '<tr><th>Bildschirm</th>';
      state.comparison.forEach(product => {
        html += `<td>${product.screen || '-'}</td>`;
      });
      html += '</tr>';

      // GPU
      html += '<tr><th>Grafikprozessor</th>';
      state.comparison.forEach(product => {
        html += `<td>${product.gpu || '-'}</td>`;
      });
      html += '</tr>';

      html += '</tbody></table></div>';

      body.innerHTML = html;
    }

    comparisonModal.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
  }

  /**
   * Hide comparison modal
   */
  function hideComparisonModal() {
    if (comparisonModal) {
      comparisonModal.classList.remove('is-visible');
      document.body.style.overflow = '';
    }
  }

  /**
   * Save comparison to sessionStorage
   */
  function saveComparison() {
    const data = Array.from(state.comparison.entries());
    sessionStorage.setItem('laptops-comparison', JSON.stringify(data));
  }

  /**
   * Load saved comparison from sessionStorage
   */
  function loadSavedComparison() {
    try {
      const saved = sessionStorage.getItem('laptops-comparison');
      if (saved) {
        const data = JSON.parse(saved);
        data.forEach(([id, product]) => {
          state.comparison.set(id, product);

          // Check the corresponding checkbox
          const checkbox = document.querySelector(
            `.laptop-card__compare-checkbox[data-product-id="${id}"]`
          );
          if (checkbox) checkbox.checked = true;
        });
        updateComparisonUI();
      }
    } catch (e) {
      console.error('Error loading comparison:', e);
    }
  }

  /**
   * Update a card's availability lamp for the selected variant option.
   * Lead time only shows for orderable variants; "Neu" products
   * (data-leadtime-only) hide every state except lead time.
   */
  function updateCardAvailability(productId, option) {
    const container = document.querySelector(
      `.laptop-card__availability[data-product-id="${productId}"]`
    );
    if (!container) return;

    const dot = container.querySelector('.laptop-card__availability-dot');
    const text = container.querySelector('.laptop-card__availability-text');
    if (!dot || !text) return;

    const status = option.dataset.availability || 'unavailable';

    if (status !== 'leadtime' && container.hasAttribute('data-leadtime-only')) {
      container.hidden = true;
      return;
    }
    container.hidden = false;

    dot.className = 'laptop-card__availability-dot';
    if (status === 'leadtime') {
      dot.classList.add('laptop-card__availability-dot--amber');
      text.textContent = `Lieferzeit ${option.dataset.weeks} Wochen`;
    } else if (status === 'store') {
      dot.classList.add('laptop-card__availability-dot--green');
      text.textContent = container.dataset.storeText;
    } else if (status === 'delivery') {
      dot.classList.add('laptop-card__availability-dot--green');
      text.textContent = container.dataset.deliveryText;
    } else {
      dot.classList.add('laptop-card__availability-dot--red');
      text.textContent = container.dataset.unavailableText;
    }
  }

  /**
   * Initialize variant selectors
   */
  function initVariantSelectors() {
    const selectors = document.querySelectorAll('.laptop-card__variant-select:not([data-init])');

    selectors.forEach(select => {
      select.setAttribute('data-init', '1');
      select.addEventListener('change', () => {
        const productId = select.dataset.productId;
        const selectedOption = select.options[select.selectedIndex];
        const price = selectedOption.dataset.price;
        const comparePrice = selectedOption.dataset.comparePrice;
        const available = selectedOption.dataset.available === 'true';
        const variantId = select.value;

        // Update price display
        const priceContainer = document.querySelector(
          `.laptop-card__price[data-product-id="${productId}"]`
        );
        if (priceContainer) {
          let priceHtml = '';
          if (comparePrice && comparePrice !== price) {
            priceHtml += `<span class="laptop-card__price-compare">${comparePrice}</span>`;
          }
          priceHtml += `<span class="laptop-card__price-current">${price}</span>`;
          priceContainer.innerHTML = priceHtml;
        }

        // Update hidden variant ID input
        const variantInput = document.querySelector(
          `.variant-id-input[data-product-id="${productId}"]`
        );
        if (variantInput) {
          variantInput.value = variantId;
        }

        // Update buy button
        const buyButton = document.querySelector(
          `.laptop-card[data-product-id="${productId}"] .laptop-card__button--buy`
        );
        if (buyButton) {
          buyButton.disabled = !available;
          buyButton.textContent = available ? 'Jetzt kaufen' : 'Ausverkauft';
        }

        // Update availability lamp
        updateCardAvailability(productId, selectedOption);

        // Update comparison data
        const compareCheckbox = document.querySelector(
          `.laptop-card__compare-checkbox[data-product-id="${productId}"]`
        );
        if (compareCheckbox) {
          compareCheckbox.dataset.productPrice = price;
        }
      });
    });
  }

  /**
   * Apply filters from URL query parameters
   * Supports: ?brand=Apple&cpu=M4 etc.
   */
  function applyUrlFilters() {
    var params = new URLSearchParams(window.location.search);
    var filterTypes = ['brand', 'cpu', 'ram', 'storage', 'screensize', 'screenresolution', 'labels'];
    var hasUrlFilters = false;

    filterTypes.forEach(function(type) {
      var value = params.get(type);
      if (!value) return;

      // Find matching checkbox (case-insensitive)
      var checkboxes = document.querySelectorAll('.laptops-filter__checkbox[data-filter-type="' + type + '"]');
      checkboxes.forEach(function(checkbox) {
        if (checkbox.dataset.filterValue.toLowerCase() === value.toLowerCase()) {
          checkbox.checked = true;
          hasUrlFilters = true;
        }
      });
    });

    if (hasUrlFilters) {
      updateFilters();
      applyFilters();
      updateActiveFilters();
      updateFilterCounts();
    }
  }

  /**
   * Initialize infinite scroll
   */
  function initInfiniteScroll() {
    var sentinel = document.querySelector('[data-infinite-scroll]');
    if (!sentinel) return;

    var currentPage = parseInt(sentinel.dataset.currentPage) || 1;
    var totalPages = parseInt(sentinel.dataset.totalPages) || 1;
    var collectionUrl = sentinel.dataset.collectionUrl;
    var sectionId = sentinel.dataset.sectionId;
    var spinner = sentinel.querySelector('[data-infinite-spinner]');
    var loading = false;

    if (currentPage >= totalPages) {
      sentinel.hidden = true;
      return;
    }

    var observer = new IntersectionObserver(function(entries) {
      if (entries[0].isIntersecting && !loading && currentPage < totalPages) {
        loadNextPage();
      }
    }, { rootMargin: '400px' });

    observer.observe(sentinel);

    function loadNextPage() {
      loading = true;
      currentPage++;
      if (spinner) spinner.hidden = false;

      var separator = collectionUrl.indexOf('?') !== -1 ? '&' : '?';
      var url = collectionUrl + separator + 'page=' + currentPage + '&section_id=' + sectionId;

      fetch(url)
        .then(function(response) { return response.text(); })
        .then(function(html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var newProducts = doc.querySelectorAll('.laptop-card');

          if (newProducts.length > 0) {
            newProducts.forEach(function(product) {
              productList.appendChild(product);
            });

            // Re-process all products for filtering
            storeProductData();
            applyFilters();

            // Initialize comparison & variant selectors for new products
            initComparison();
            initVariantSelectors();
            loadSavedComparison();
          }

          if (currentPage >= totalPages) {
            observer.disconnect();
            sentinel.hidden = true;
          }
        })
        .catch(function(error) {
          console.error('Error loading more products:', error);
        })
        .finally(function() {
          loading = false;
          if (spinner) spinner.hidden = true;
        });
    }
  }

  /**
   * Collection search bar (replaces filters when search_mode is enabled)
   */
  function initCollectionSearch() {
    const searchInput = document.getElementById('collection-search');
    const clearBtn = document.getElementById('collection-search-clear');
    const countEl = document.getElementById('collection-search-count');

    if (!searchInput) return;

    function runSearch() {
      const query = searchInput.value.trim().toLowerCase();

      // Collect active filter checkboxes (Hersteller / Typ in search mode)
      const activeFilters = {};
      document.querySelectorAll('.laptops-search__filters .laptops-filter__checkbox:checked').forEach(function(cb) {
        const type = cb.dataset.filterType;
        if (!activeFilters[type]) activeFilters[type] = [];
        activeFilters[type].push(cb.dataset.filterValue.toLowerCase());
      });

      const products = productList.querySelectorAll('.laptop-card');
      let visible = 0;

      products.forEach(function(card) {
        const title = (card.querySelector('.laptop-card__title')
          || card.querySelector('h2, h3, .laptop-card__name')
          || card).textContent.toLowerCase();
        const brand = (card.dataset.brand || '').toLowerCase();
        const type  = (card.dataset.type  || '').toLowerCase();

        const matchesSearch = !query || title.includes(query) || brand.includes(query) || type.includes(query);
        const matchesBrand  = !activeFilters.brand || activeFilters.brand.includes(brand);
        const matchesType   = !activeFilters.type  || activeFilters.type.includes(type);

        const show = matchesSearch && matchesBrand && matchesType;
        card.style.display = show ? '' : 'none';
        if (show) visible++;
      });

      if (clearBtn) clearBtn.hidden = !query;
      if (countEl) {
        const hasQuery = query || Object.keys(activeFilters).length > 0;
        countEl.textContent = hasQuery
          ? visible + ' Produkt' + (visible !== 1 ? 'e' : '') + ' gefunden'
          : '';
      }

      const emptyState = document.querySelector('.laptops-products__empty');
      if (emptyState) emptyState.style.display = visible === 0 ? 'block' : 'none';
    }

    searchInput.addEventListener('input', runSearch);

    // Filter checkboxes inside the search bar also trigger runSearch
    document.querySelectorAll('.laptops-search__filters .laptops-filter__checkbox').forEach(function(cb) {
      cb.addEventListener('change', runSearch);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function() {
        searchInput.value = '';
        runSearch();
        searchInput.focus();
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
